import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { GET_CLINICIAN } from "../../queries/clinicianQueries";
import { getToken } from "../../classes/utils/TokenUtils";

export function useClinician() {
  const [clinician, setClinician] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const lastTokenRef = useRef(null);

  const [getClinician] = useLazyQuery(GET_CLINICIAN, {
    fetchPolicy: "cache-first",
  });

  const fetchClinician = useCallback(async () => {
    // Don't fetch if no token available yet
    const token = getToken();
    if (!token) {
      return;
    }

    // Debug: decode and log JWT claims
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
    } catch (e) {
    }

    // If token changed, reset state
    if (lastTokenRef.current && lastTokenRef.current !== token) {
      setClinician(null);
      setError(null);
      hasFetchedRef.current = false;
    }
    lastTokenRef.current = token;

    try {
      setLoading(true);
      setError(null);

      const { data } = await getClinician({
        fetchPolicy: "network-only",
      });

      setClinician(data?.clinicianByOwner || null);
      hasFetchedRef.current = true;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [getClinician]);

  const refreshClinician = useCallback(async () => {
    await fetchClinician();
  }, [fetchClinician]);

  // Reset state (useful when user logs out)
  const resetClinician = useCallback(() => {
    setClinician(null);
    setError(null);
    setLoading(true);
    hasFetchedRef.current = false;
    lastTokenRef.current = null;
  }, []);

  // Poll for token availability and fetch once ready
  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }

    const token = getToken();
    if (token) {
      fetchClinician();
      return;
    }

    // Token not ready yet, check periodically
    const interval = setInterval(() => {
      const token = getToken();
      if (token && !hasFetchedRef.current) {
        clearInterval(interval);
        fetchClinician();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [fetchClinician]);

  return {
    clinician,
    loading,
    error,
    refreshClinician,
    resetClinician,
  };
}
